/* eslint-disable no-undef */
(function () {
  'use strict';

  window.OletLens = window.OletLens || {};

  const Config = window.OletLens.Config || {};

  async function getCart() {
    const res = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error('Cart API error: ' + res.status);
    }
    return res.json();
  }

  function groupByBundle(cartItems) {
    const map = {};
    (cartItems || []).forEach(function (item) {
      const props = item.properties || {};
      const key = props[Config.PROPS.BUNDLE_KEY];
      const role = props[Config.PROPS.BUNDLE_ROLE];
      if (!key || !role) {
        return;
      }
      if (!map[key]) {
        map[key] = {
          frame: null,
          lensBase: null,
          addons: [],
        };
      }
      if (role === Config.BUNDLE_ROLES.FRAME) {
        map[key].frame = item;
      } else if (role === Config.BUNDLE_ROLES.LENS_BASE) {
        map[key].lensBase = item;
      } else if (role === Config.BUNDLE_ROLES.ADDON) {
        map[key].addons.push(item);
      }
    });
    return map;
  }

  async function runAutoClean() {
    const cart = await getCart();
    const groups = groupByBundle(cart.items);

    const itemsToRemove = [];
    const itemsToResetQty = [];

    Object.keys(groups).forEach(function (key) {
      const group = groups[key];

      var hasFrame = !!group.frame;
      var hasLensBase = !!group.lensBase;

      if (!hasLensBase && !hasFrame) {
        (group.addons || []).forEach(function (addon) {
          itemsToRemove.push(addon);
        });
      }

      if (hasLensBase && !hasFrame) {
        itemsToRemove.push(group.lensBase);
        (group.addons || []).forEach(function (addon) {
          itemsToRemove.push(addon);
        });
      }

      if (!hasLensBase && hasFrame) {
        (group.addons || []).forEach(function (addon) {
          itemsToRemove.push(addon);
        });
      }

      [group.frame, group.lensBase].concat(group.addons || []).forEach(function (item) {
        if (item && item.quantity && item.quantity > 1) {
          itemsToResetQty.push(item);
        }
      });
    });

    var polyLensIds = Config.POLYCARBONATE_LENS_VARIANT_IDS || [];
    var sunAddonIds = Config.SUN_UPGRADE_ADDON_VARIANT_IDS || [];

    function isPolycarbonateLensVariant(variantId) {
      var n = Number(variantId);
      return polyLensIds.indexOf(n) !== -1;
    }

    function isSunUpgradeVariant(variantId) {
      var n = Number(variantId);
      return sunAddonIds.indexOf(n) !== -1;
    }

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      if (!group.lensBase || !isPolycarbonateLensVariant(group.lensBase.variant_id)) {
        return;
      }
      (group.addons || []).forEach(function (addon) {
        if (isSunUpgradeVariant(addon.variant_id)) {
          itemsToRemove.push(addon);
        }
      });
    });

    var bifocalBadIds = Config.BIFOCAL_INCOMPATIBLE_ADDON_VARIANT_IDS || [];

    function isBifocalIncompatibleVariant(variantId) {
      var n = Number(variantId);
      return bifocalBadIds.indexOf(n) !== -1;
    }

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      if (!group.lensBase) return;
      var usage =
        group.lensBase.properties &&
        group.lensBase.properties[Config.PROPS.USAGE_TYPE];
      var isReadingBifocalLine =
        usage === 'reading' &&
        (Config.READING_BIFOCAL_VARIANT_IDS || []).indexOf(Number(group.lensBase.variant_id)) !== -1;
      if (usage !== 'bifocals' && !isReadingBifocalLine) return;
      (group.addons || []).forEach(function (addon) {
        if (isBifocalIncompatibleVariant(addon.variant_id)) {
          itemsToRemove.push(addon);
        }
      });
    });

    const updates = {};
    itemsToRemove.forEach(function (item) {
      updates[item.key] = 0;
    });
    itemsToResetQty.forEach(function (item) {
      if (!updates[item.key]) {
        updates[item.key] = 1;
      }
    });

    if (Object.keys(updates).length === 0) {
      return { corrected: false, messages: [] };
    }

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    return {
      corrected: true,
      messages: ['We updated your cart to keep lens selections consistent.'],
    };
  }

  async function removeBundleKeepFrame(bundleKey) {
    const cart = await getCart();
    const groups = groupByBundle(cart.items);
    const group = groups[bundleKey];
    if (!group) {
      return;
    }
    const updates = {};
    if (group.lensBase) {
      updates[group.lensBase.key] = 0;
    }
    (group.addons || []).forEach(function (addon) {
      updates[addon.key] = 0;
    });
    // Also remove the existing framed line with bundle properties;
    // we'll re-add a clean frame-only line right after.
    if (group.frame) {
      updates[group.frame.key] = 0;
    }
    if (Object.keys(updates).length === 0) {
      return;
    }
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (group.frame) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: group.frame.variant_id,
              quantity: group.frame.quantity || 1,
            },
          ],
        }),
      });
    }
  }

  async function removeEntireBundle(bundleKey) {
    const cart = await getCart();
    const groups = groupByBundle(cart.items);
    const group = groups[bundleKey];
    if (!group) {
      return;
    }
    const updates = {};
    [group.frame, group.lensBase].concat(group.addons || []).forEach(function (item) {
      if (item) {
        updates[item.key] = 0;
      }
    });
    if (Object.keys(updates).length === 0) {
      return;
    }
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
  }

  async function getBundleState(bundleKey) {
    const cart = await getCart();
    const groups = groupByBundle(cart.items);
    return groups[bundleKey] || null;
  }

  window.OletLens.BundleEngine = {
    getCart,
    groupByBundle,
    runAutoClean,
    removeBundleKeepFrame,
    removeEntireBundle,
    getBundleState,
  };
})();

